import { useEffect, useState } from "react";

import { useSelector } from "react-redux";

import { proNavigator } from "single-spa/react-app/ProNavigator";

import { getColumns, getIndustryColumn, INDUSTRY_COLUMN_FIELDS, selectColumn } from "../configuration/table-columns";
import { selectIsRowsSelectionEnabled, selectServesIndustryTaxonomy } from "../store/selectors";
import type { CompetitiveColumnType } from "../types/common";

import useCompetitiveFilter from "./useCompetitiveFilter";
import useCompetitiveQueryParams from "./useCompetitiveQueryParams";

type HookReturnType = {
    columns: readonly CompetitiveColumnType[];
    onClickToggleColumns: (selectedColumnIndex: number) => void;
    onSort: ({ field, sortDirection }: { field: string; sortDirection: string }) => void;
};

export const useCompetitiveColumns = (isTrialUser: boolean): HookReturnType => {
    const isRowsSelectionEnabled = useSelector(selectIsRowsSelectionEnabled);
    const showsIndustryTaxonomy = useSelector(selectServesIndustryTaxonomy);

    const { sortDirection, field, selectedSite } = useCompetitiveQueryParams();

    const { onSelectCategory, onSelectIndustryTaxonomy } = useCompetitiveFilter();

    const handlers = { onSelectCategory, onSelectIndustryTaxonomy };

    const [columns, setColumns] = useState(() => getColumns(handlers, selectedSite, showsIndustryTaxonomy));

    // Which classification is served arrives with the results, so it is not settled at mount. Only the
    // industry slot is re-seeded: rebuilding the array would drop the user's other column toggles, and
    // the picker keys off index, so the column has to be swapped where it already sits.
    useEffect(() => {
        setColumns((current) =>
            current.map((column) =>
                column.field !== undefined && INDUSTRY_COLUMN_FIELDS.includes(column.field)
                    ? { ...getIndustryColumn(handlers, showsIndustryTaxonomy), visible: column.visible }
                    : column,
            ),
        );
    }, [showsIndustryTaxonomy]);

    useEffect(() => {
        if (isRowsSelectionEnabled) {
            setColumns((columns) => [selectColumn, ...columns]);
        } else {
            setColumns((columns) => columns.filter((column) => column.field !== "select"));
        }
    }, [isRowsSelectionEnabled]);

    const onClickToggleColumns = (selectedColumnIndex: number) => {
        setColumns(
            columns.map((column, index: number) => {
                if (index === selectedColumnIndex) {
                    return { ...column, visible: !column.visible };
                }
                return column;
            }),
        );
    };

    const onSort = ({ field, sortDirection }: { field: string; sortDirection: string }) => {
        if (isTrialUser) {
            return false;
        }

        proNavigator.updateParams({
            similarsites_orderby: `${field} ${sortDirection}`,
        });
    };

    return {
        columns: columns.map((column: CompetitiveColumnType) => ({
            ...column,
            sortable: isTrialUser ? false : column.sortable,
            isSorted: column.field === field,
            sortDirection,
        })),
        onClickToggleColumns,
        onSort,
    };
};
